import { colors, Indent, Stack, Typography, useModal } from '@ui-components/neo.tax';
import { ReactElement } from 'react';

import { RdProject } from '@neo.tax/rd/hooks';
import { HIGHLIGHT_VARIANT } from '@neo.tax/shared';

import { RdProjectBusinessComponentModal } from '../rd-project-business-component-modal';
import { RdProjectDisallowancesModal } from '../rd-project-disallowances-modal';
import { RdProjectEligibilityReview } from '../rd-project-eligibility-review';
import { RdProjectProcessOfExperimentationModal } from '../rd-project-process-of-experimentation-modal';
import { RdProjectPurposeModal } from '../rd-project-purpose-modal';
import { RdProjectTechnicalChallengesModal } from '../rd-project-technical-challenges-modal';
import { IrsSectionElectionToggles } from './IrsSectionElectionToggles';
import { ProjectEligibilityContainer } from './rd-project-eligibility.styled';
import { RdProjectEligibilityItem } from './RdProjectEligibilityItem';
import {
  purposeProps,
  disallowanceItemProps,
  processOfExperimentationProps,
  technicalChallengesProps,
  businessComponentProps,
} from './utils';

interface RdProjectEligibilityProps {
  rdProject: RdProject;
}

export const RdProjectEligibility = ({ rdProject }: RdProjectEligibilityProps): ReactElement => {
  const { isOpen: isDisallowancesModalOpen, setIsOpen: setIsOpenDisallowancesModal } = useModal();
  const { isOpen: isTechnicalChallengesModalOpen, setIsOpen: setIsOpenTechnicalChallengesModal } = useModal();
  const { isOpen: isProcessOfExperimentationModalOpen, setIsOpen: setIsOpenProcessOfExperimentationModal } = useModal();
  const { isOpen: isPurposeModalOpen, setIsOpen: setIsOpenPurposeModal } = useModal();
  const { isOpen: isBusinessComponentModalOpen, setIsOpen: setIsOpenBusinessComponentModal } = useModal();

  return (
    <>
      <ProjectEligibilityContainer direction="column" paddingX={4} paddingY={4} gap={8} overflow="auto">
        <RdProjectEligibilityReview rdProject={rdProject} />

        <IrsSectionElectionToggles rdProject={rdProject} />

        {rdProject.consideredForRd && (
          <Indent border>
            <Stack direction="column" gap={5} paddingY={10}>
              <Stack direction="column" gap={2}>
                <Typography fontSize="lg" fontWeight="medium" color={colors.gray[400]}>
                  Additional Details for Export
                </Typography>
                <Typography fontSize="sm" color={colors.gray[400]}>
                  We are required to gather some additional information to prepare your supporting documents
                </Typography>
              </Stack>

              <Stack direction="column">
                <RdProjectEligibilityItem
                  variant={HIGHLIGHT_VARIANT}
                  onClick={(): void => setIsOpenDisallowancesModal(true)}
                  title="Disallowances"
                  subtitle="Confirm project qualification"
                  {...disallowanceItemProps(rdProject)}
                />
                <RdProjectEligibilityItem
                  variant={HIGHLIGHT_VARIANT}
                  onClick={(): void => setIsOpenPurposeModal(true)}
                  title="Purpose"
                  subtitle="What/why was being worked on?"
                  borderTop
                  {...purposeProps(rdProject)}
                />
                <RdProjectEligibilityItem
                  variant={HIGHLIGHT_VARIANT}
                  onClick={(): void => setIsOpenBusinessComponentModal(true)}
                  title="Business Component"
                  subtitle="What part was being worked on?"
                  borderTop
                  {...businessComponentProps(rdProject)}
                />
                <RdProjectEligibilityItem
                  variant={HIGHLIGHT_VARIANT}
                  onClick={(): void => setIsOpenTechnicalChallengesModal(true)}
                  title="Technical Challenges"
                  subtitle="Describe the technical uncertainties"
                  borderTop
                  {...technicalChallengesProps(rdProject)}
                />
                <RdProjectEligibilityItem
                  variant={HIGHLIGHT_VARIANT}
                  onClick={(): void => setIsOpenProcessOfExperimentationModal(true)}
                  title="Process of Experimentation"
                  subtitle="Describe the process of experimentation"
                  borderTop
                  {...processOfExperimentationProps(rdProject)}
                />
              </Stack>
            </Stack>
          </Indent>
        )}
      </ProjectEligibilityContainer>

      {/* Modals */}
      <RdProjectDisallowancesModal
        rdProject={rdProject}
        isOpen={isDisallowancesModalOpen}
        onClose={(): void => setIsOpenDisallowancesModal(false)}
      />
      <RdProjectPurposeModal
        rdProject={rdProject}
        isOpen={isPurposeModalOpen}
        onClose={(): void => setIsOpenPurposeModal(false)}
      />
      <RdProjectBusinessComponentModal
        rdProject={rdProject}
        isOpen={isBusinessComponentModalOpen}
        onClose={(): void => setIsOpenBusinessComponentModal(false)}
      />
      <RdProjectTechnicalChallengesModal
        rdProject={rdProject}
        isOpen={isTechnicalChallengesModalOpen}
        onClose={(): void => setIsOpenTechnicalChallengesModal(false)}
      />
      <RdProjectProcessOfExperimentationModal
        rdProject={rdProject}
        isOpen={isProcessOfExperimentationModalOpen}
        onClose={(): void => setIsOpenProcessOfExperimentationModal(false)}
      />
    </>
  );
};
